/**
 * Sidecar metadata: a small, dependency-free XML reader and the mapping from
 * sidecar elements to metadata fields used by watch folders.
 *
 * The reader handles what metadata sidecars contain (elements, attributes,
 * text, CDATA, comments, processing instructions, the predefined and numeric
 * entities). It refuses DOCTYPE declarations, so no external entities or
 * entity expansion are ever processed.
 *
 * NLE timelines (Check in, 1.0.0-rc.7) read through it too, with limits of
 * their own (size, nesting depth, element count) and a bare DOCTYPE allowed
 * (`<!DOCTYPE fcpxml>`, `<!DOCTYPE xmeml>`, as every NLE writes): one without
 * an internal subset, so still nothing is declared, fetched or expanded.
 */
import { validateMetadata, type FieldDefinition, type MetadataSchemaDefinition } from './domain.ts';
import { normalizeDateTime } from './date-schedule.ts';

export interface XmlElement {
  name: string;
  /** Name without a namespace prefix. */
  local: string;
  attributes: Record<string, string>;
  children: XmlElement[];
  /** Concatenated character data directly inside this element (trimmed). */
  text: string;
  /**
   * 1.9.0, with `keepNodes` (TTML): the attributes by their qualified names as written (`tts:color`, `xmlns:tts`,
   * `xml:lang`), so namespaces can be resolved. `attributes` stays keyed by local name.
   */
  qattributes?: Record<string, string>;
  /**
   * 1.9.0, with `keepNodes` (TTML): the content in document order, text untrimmed (CDATA as text), elements as the
   * same objects as in `children`. `space` is the in-scope xml:space ('preserve' or 'default').
   */
  nodes?: XmlNode[];
  space?: 'default' | 'preserve';
}

/** One piece of an element's content (`XmlElement.nodes`). */
export type XmlNode = { text: string } | { element: XmlElement };

export class XmlError extends Error {
  override name = 'XmlError';
}

function decode(s: string): string {
  return s.replace(/&(#x[0-9a-fA-F]+|#\d+|lt|gt|amp|quot|apos);/g, (_m, e: string) => {
    if (e === 'lt') return '<';
    if (e === 'gt') return '>';
    if (e === 'amp') return '&';
    if (e === 'quot') return '"';
    if (e === 'apos') return "'";
    const code = e.startsWith('#x') ? parseInt(e.slice(2), 16) : parseInt(e.slice(1), 10);
    return Number.isFinite(code) && code > 0 && code <= 0x10ffff ? String.fromCodePoint(code) : '';
  });
}

const localName = (n: string) => n.slice(n.indexOf(':') + 1);

export interface XmlLimits {
  /** Largest input, in characters (default 5 MB). */
  maxBytes?: number;
  /** Deepest nesting of elements (default 64). */
  maxDepth?: number;
  /** Most elements in the document (default: no limit beyond the size). */
  maxElements?: number;
  /** Accept a DOCTYPE without an internal subset (`<!DOCTYPE fcpxml>`); entity declarations are refused either way. */
  allowDoctype?: boolean;
  /** What the input is, for messages ("sidecar" by default). */
  what?: string;
  /** 1.9.0: also fill `qattributes`, `nodes` and `space` (TTML needs the order of text and elements). */
  keepNodes?: boolean;
}

/** The limits NLE timelines are read with (Check in, 1.0.0-rc.7). */
export const TIMELINE_XML_LIMITS: XmlLimits = { maxBytes: 32 * 1024 * 1024, maxDepth: 128, maxElements: 500_000, allowDoctype: true, what: 'timeline' };

export function parseXml(input: string, limits: XmlLimits = {}): XmlElement {
  const what = limits.what ?? 'sidecar';
  const maxBytes = limits.maxBytes ?? 5 * 1024 * 1024;
  const maxDepth = limits.maxDepth ?? 64;
  const maxElements = limits.maxElements ?? Number.MAX_SAFE_INTEGER;
  let elements = 0;
  let doctype = false;
  const keep = limits.keepNodes === true;
  const src = input.replace(/^﻿/, '');
  if (src.length > maxBytes) throw new XmlError(`The ${what} is larger than ${Math.round(maxBytes / (1024 * 1024))} MB`);
  let i = 0;
  const stack: Array<XmlElement & { raw: string[] }> = [];
  let root: XmlElement | null = null;
  const top = () => stack[stack.length - 1];
  while (i < src.length) {
    const lt = src.indexOf('<', i);
    const textEnd = lt === -1 ? src.length : lt;
    if (textEnd > i) {
      const text = src.slice(i, textEnd);
      if (top()) {
        const t = decode(text);
        top()!.raw.push(t);
        if (keep) top()!.nodes!.push({ text: t });
      }
      else if (text.trim()) throw new XmlError('Text outside the root element');
    }
    if (lt === -1) break;
    if (src.startsWith('<!--', lt)) {
      const end = src.indexOf('-->', lt + 4);
      if (end === -1) throw new XmlError('Unterminated comment');
      i = end + 3;
    } else if (src.startsWith('<![CDATA[', lt)) {
      const end = src.indexOf(']]>', lt + 9);
      if (end === -1) throw new XmlError('Unterminated CDATA section');
      top()?.raw.push(src.slice(lt + 9, end));
      if (keep && top()) top()!.nodes!.push({ text: src.slice(lt + 9, end) });
      i = end + 3;
    } else if (src.startsWith('<?', lt)) {
      const end = src.indexOf('?>', lt + 2);
      if (end === -1) throw new XmlError('Unterminated processing instruction');
      i = end + 2;
    } else if (src.startsWith('<!', lt)) {
      // A bare DOCTYPE (a name, optionally a PUBLIC or SYSTEM identifier) before the root, when allowed: never an
      // internal subset, so no entity is ever declared. External identifiers are never fetched.
      const end = src.indexOf('>', lt);
      const decl = end === -1 ? '' : src.slice(lt, end + 1);
      const bare = /^<!DOCTYPE\s+[A-Za-z_][\w.:-]*(\s+(PUBLIC\s+("[^"]*"|'[^']*')\s+("[^"]*"|'[^']*')|SYSTEM\s+("[^"]*"|'[^']*')))?\s*>$/.test(decl);
      if (!limits.allowDoctype || !bare || doctype || root || stack.length) throw new XmlError(`DOCTYPE and entity declarations are not accepted in ${what}s`);
      doctype = true;
      i = end + 1;
    } else if (src[lt + 1] === '/') {
      const end = src.indexOf('>', lt);
      if (end === -1) throw new XmlError('Unterminated closing tag');
      const name = src.slice(lt + 2, end).trim();
      const el = stack.pop();
      if (!el || el.name !== name) throw new XmlError(`Unexpected closing tag </${name}>`);
      el.text = el.raw.join('').trim();
      const { raw: _raw, ...clean } = el;
      void _raw;
      if (top()) {
        top()!.children.push(clean);
        if (keep) top()!.nodes!.push({ element: clean });
      } else root = clean;
      i = end + 1;
    } else {
      // Start tag: name, attributes, optional self-closing slash. Quotes may contain '>'.
      let j = lt + 1;
      let quote: string | null = null;
      for (; j < src.length; j++) {
        const c = src[j]!;
        if (quote) { if (c === quote) quote = null; } else if (c === '"' || c === "'") quote = c; else if (c === '>') break;
      }
      if (j >= src.length) throw new XmlError('Unterminated tag');
      let body = src.slice(lt + 1, j);
      const selfClosing = body.endsWith('/');
      if (selfClosing) body = body.slice(0, -1);
      const m = /^([^\s/>]+)/.exec(body);
      if (!m) throw new XmlError('Malformed tag');
      const name = m[1]!;
      const attributes: Record<string, string> = {};
      const qattributes: Record<string, string> = {};
      const attrRe = /([^\s=]+)\s*=\s*("([^"]*)"|'([^']*)')/g;
      let a: RegExpExecArray | null;
      while ((a = attrRe.exec(body.slice(name.length)))) {
        const v = decode(a[3] ?? a[4] ?? '');
        attributes[localName(a[1]!)] = v;
        if (keep) qattributes[a[1]!] = v;
      }
      if (root && !stack.length) throw new XmlError('More than one root element');
      const el: XmlElement & { raw: string[] } = { name, local: localName(name), attributes, children: [] as XmlElement[], text: '', raw: [] as string[] };
      if (keep) {
        const sp = qattributes['xml:space'];
        el.qattributes = qattributes;
        el.nodes = [];
        el.space = sp === 'preserve' || sp === 'default' ? sp : (top()?.space ?? 'default');
      }
      if (stack.length > maxDepth) throw new XmlError(`The ${what} is nested too deeply`);
      if (++elements > maxElements) throw new XmlError(`The ${what} has more than ${maxElements} elements`);
      if (selfClosing) {
        const { raw: _raw, ...clean } = el;
        void _raw;
        if (top()) {
          top()!.children.push(clean);
          if (keep) top()!.nodes!.push({ element: clean });
        } else root = clean;
      } else {
        stack.push(el);
      }
      i = j + 1;
    }
  }
  if (stack.length) throw new XmlError(`Missing closing tag for <${top()!.name}>`);
  if (!root) throw new XmlError('No root element');
  return root;
}

/** Every element in document order. */
export function allElements(root: XmlElement): Array<{ el: XmlElement; path: string[] }> {
  const out: Array<{ el: XmlElement; path: string[] }> = [];
  const walk = (el: XmlElement, path: string[]) => {
    const p = [...path, el.local];
    out.push({ el, path: p });
    el.children.forEach((c) => walk(c, p));
  };
  walk(root, []);
  return out;
}

export interface SidecarMapping {
  /** Element local name (e.g. "Title") or a slash path suffix (e.g. "Clip/Description"). Attributes: "Clip/@title". */
  element: string;
  fieldId: string;
}

export interface SidecarResult {
  values: Record<string, unknown>;
  /** The asset (placeholder) ID the sidecar names, upper-cased; null when it names none. */
  assetCode: string | null;
  /**
   * 1.2.0, the reserved keys (SIDECAR_RESERVED): the asset class (a class's name or key), the master variant (a
   * variant's name or code) and the asset ID this file is a further master of, as written (trimmed); null when absent.
   * Resolving them (and dropping unknown ones with a note) is the watch folder's job. Optional until filled in.
   */
  assetClass?: string | null;
  masterVariant?: string | null;
  masterOf?: string | null;
  /** Fields that received a value, with the element each came from. */
  matched: Array<{ fieldId: string; element: string }>;
  problems: string[];
}

const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');

/** The mapping target that names the asset a sidecar belongs to (instead of a metadata field). */
export const SIDECAR_ASSET_ID_TARGET = '_asset_id';

/** Element or key names read as the asset ID when the mapping has no `_asset_id` entry (case, `_` and `-` ignored). */
export const SIDECAR_ASSET_ID_NAMES = ['AssetId', 'asset_id', 'assetId', 'PRISM_ASSET_ID'] as const;
const ASSET_ID_NAMES = new Set(SIDECAR_ASSET_ID_NAMES.map(norm));

/**
 * 1.2.0: further reserved sidecar keys, in XML and JSON, matched like field names (case and punctuation ignored):
 * AssetClass (a class's name or key), MasterVariant (a variant's name or code) and MasterOf (the asset ID this file is
 * a further master of). Each has a mapping target, so an explicit mapping can map another element onto it.
 */
export const SIDECAR_ASSET_CLASS_TARGET = '_asset_class';
export const SIDECAR_MASTER_VARIANT_TARGET = '_master_variant';
export const SIDECAR_MASTER_OF_TARGET = '_master_of';
export const SIDECAR_RESERVED = {
  assetClass: { target: SIDECAR_ASSET_CLASS_TARGET, names: ['AssetClass', 'asset_class', 'assetClass'] },
  masterVariant: { target: SIDECAR_MASTER_VARIANT_TARGET, names: ['MasterVariant', 'master_variant', 'masterVariant'] },
  masterOf: { target: SIDECAR_MASTER_OF_TARGET, names: ['MasterOf', 'master_of', 'masterOf'] },
} as const;
/** Every mapping target that is not a metadata field. */
export const SIDECAR_TARGETS = [SIDECAR_ASSET_ID_TARGET, SIDECAR_ASSET_CLASS_TARGET, SIDECAR_MASTER_VARIANT_TARGET, SIDECAR_MASTER_OF_TARGET] as const;

/** Convert sidecar text to a field's value type; null when it cannot be converted. */
export function coerceFieldValue(field: FieldDefinition, texts: string[]): unknown {
  const values = texts.map((t) => t.trim()).filter(Boolean);
  if (!values.length) return null;
  const first = values[0]!;
  switch (field.type) {
    case 'text':
    case 'timecode':
      return first;
    case 'rich_text':
      return values.join('\n\n');
    case 'number': {
      const n = Number(first.replace(',', '.'));
      return Number.isFinite(n) ? n : null;
    }
    case 'boolean':
      if (/^(true|yes|y|1|on)$/i.test(first)) return true;
      if (/^(false|no|n|0|off)$/i.test(first)) return false;
      return null;
    case 'datetime': {
      // 1.0.0-rc.10: an ISO 8601 instant with Z or an offset (a time without one is ambiguous and dropped).
      const iso = normalizeDateTime(first);
      if (iso) return iso;
      const d = /^\d{4}-\d{2}-\d{2}$/.test(first) ? normalizeDateTime(`${first}T00:00:00Z`) : null;
      return d;
    }
    case 'date': {
      const m = /^(\d{4})-?(\d{2})-?(\d{2})/.exec(first);
      if (m) return `${m[1]}-${m[2]}-${m[3]}`;
      const d = new Date(first);
      return Number.isNaN(d.getTime()) ? null : d.toISOString().slice(0, 10);
    }
    case 'vocabulary': {
      const pick = (v: string) => field.options?.find((o) => o.value.toLowerCase() === v.toLowerCase() || o.label.toLowerCase() === v.toLowerCase())?.value;
      if (field.multiple) {
        const list = values.flatMap((v) => v.split(/[,;]/)).map((v) => pick(v.trim())).filter((v): v is string => Boolean(v));
        return list.length ? [...new Set(list)] : null;
      }
      return pick(first) ?? null;
    }
    case 'tags': {
      const tags = values.flatMap((v) => v.split(/[,;\n]/)).map((t) => t.trim()).filter(Boolean);
      return tags.length ? [...new Set(tags)] : null;
    }
  }
}

/**
 * Map a sidecar document to metadata values. With an explicit mapping only the
 * listed elements are used; otherwise an element whose name matches a field's
 * id (without "f_") or label is used, e.g. <Title>, <Description>, <Keywords>.
 * Values that do not fit the model are dropped and reported, never forced.
 */
export function mapSidecarXml(xml: string, def: MetadataSchemaDefinition, mapping: SidecarMapping[] = []): SidecarResult {
  return mapSidecarTree(parseXml(xml), def, mapping);
}

/**
 * A JSON sidecar as an element tree, so it maps exactly like XML: object keys
 * are elements, arrays repeat their key's element (["a","b"] under "keywords"
 * reads like <keywords><keyword>a</keyword>…), and numbers and booleans become text.
 */
export function jsonToElement(value: unknown, name = 'sidecar', depth = 0): XmlElement {
  if (depth > 64) throw new XmlError('The sidecar is nested too deeply');
  const el: XmlElement = { name, local: name, attributes: {}, children: [], text: '' };
  if (value === null || value === undefined) return el;
  if (Array.isArray(value)) {
    const item = name.endsWith('s') ? name.slice(0, -1) : 'item';
    el.children = value.map((v) => jsonToElement(v, item, depth + 1));
    return el;
  }
  if (typeof value === 'object') {
    el.children = Object.entries(value as Record<string, unknown>).map(([k, v]) => jsonToElement(v, k, depth + 1));
    return el;
  }
  el.text = String(value).trim();
  return el;
}

/** Map a JSON sidecar (an object) to metadata values, with the same rules as XML. */
export function mapSidecarJson(json: string, def: MetadataSchemaDefinition, mapping: SidecarMapping[] = []): SidecarResult {
  if (json.length > 5 * 1024 * 1024) throw new XmlError('The sidecar is larger than 5 MB');
  let value: unknown;
  try {
    value = JSON.parse(json.replace(/^\uFEFF/, ''));
  } catch (err) {
    throw new XmlError(`Not valid JSON: ${(err as Error).message}`);
  }
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw new XmlError('A JSON sidecar must be an object, e.g. { "title": "…" }');
  return mapSidecarTree(jsonToElement(value), def, mapping);
}

function mapSidecarTree(root: XmlElement, def: MetadataSchemaDefinition, mapping: SidecarMapping[]): SidecarResult {
  const problems: string[] = [];
  const matched: SidecarResult['matched'] = [];
  const texts = new Map<string, { element: string; texts: string[] }>();
  const elements = allElements(root);
  let assetCodeTexts: string[] = [];
  /** 1.2.0: the reserved keys' texts, by mapping target. */
  const reservedTexts = new Map<string, string[]>();
  const reservedTargets = new Set<string>([SIDECAR_ASSET_CLASS_TARGET, SIDECAR_MASTER_VARIANT_TARGET, SIDECAR_MASTER_OF_TARGET]);

  /** Texts of an element: its own text, or its children's when it only groups them (<Keywords><Keyword/>…). */
  const textsOf = (el: XmlElement) => (el.text ? [el.text] : el.children.map((c) => c.text).filter(Boolean));

  if (mapping.length) {
    for (const m of mapping) {
      const field = m.fieldId === SIDECAR_ASSET_ID_TARGET || reservedTargets.has(m.fieldId) ? null : def.fields.find((f) => f.id === m.fieldId);
      if (m.fieldId === SIDECAR_ASSET_ID_TARGET || reservedTargets.has(m.fieldId)) {
        const [elPath, attr] = m.element.split('/@') as [string, string | undefined];
        const want = elPath.split('/').filter(Boolean).map(norm);
        const hits = elements.filter(({ path }) => want.length && want.every((w, k) => norm(path[path.length - want.length + k] ?? '') === w));
        const found = hits.flatMap(({ el }) => (attr ? (el.attributes[attr] !== undefined ? [el.attributes[attr]!] : []) : textsOf(el)));
        if (m.fieldId === SIDECAR_ASSET_ID_TARGET) assetCodeTexts.push(...found);
        else reservedTexts.set(m.fieldId, [...(reservedTexts.get(m.fieldId) ?? []), ...found]);
        continue;
      }
      if (!field) { problems.push(`Mapping for ${m.element} names an unknown field ${m.fieldId}`); continue; }
      const [elPath, attr] = m.element.split('/@') as [string, string | undefined];
      const want = elPath.split('/').filter(Boolean).map(norm);
      const hits = elements.filter(({ path }) => want.length && want.every((w, k) => norm(path[path.length - want.length + k] ?? '') === w));
      const found = hits.flatMap(({ el }) => (attr ? (el.attributes[attr] !== undefined ? [el.attributes[attr]!] : []) : textsOf(el)));
      if (found.length) {
        const cur = texts.get(field.id) ?? { element: m.element, texts: [] };
        cur.texts.push(...found);
        texts.set(field.id, cur);
      }
    }
  }
  if (!mapping.some((m) => m.fieldId === SIDECAR_ASSET_ID_TARGET)) {
    const hit = elements.find(({ el, path }) => path.length > 1 && ASSET_ID_NAMES.has(norm(el.local)) && textsOf(el).length);
    assetCodeTexts = hit ? textsOf(hit.el) : [];
  }
  // 1.2.0: AssetClass, MasterVariant and MasterOf by their reserved names, unless the mapping names an element for them.
  const reservedNames = new Set<string>();
  for (const r of Object.values(SIDECAR_RESERVED)) {
    const names = new Set(r.names.map(norm));
    names.forEach((n) => reservedNames.add(n));
    if (mapping.some((m) => m.fieldId === r.target)) continue;
    const hit = elements.find(({ el, path }) => path.length > 1 && names.has(norm(el.local)) && textsOf(el).length);
    if (hit) reservedTexts.set(r.target, textsOf(hit.el));
  }
  const reserved = (target: string) => (reservedTexts.get(target) ?? []).map((t) => t.trim()).find(Boolean) ?? null;
  if (!mapping.length) {
    for (const field of def.fields) {
      const names = new Set([norm(field.id.replace(/^f_/, '')), norm(field.label)]);
      const hit = elements.find(({ el }) => names.has(norm(el.local)) && !reservedNames.has(norm(el.local)) && textsOf(el).length);
      if (hit) texts.set(field.id, { element: hit.path.join('/'), texts: textsOf(hit.el) });
    }
  }

  const assetCode = assetCodeTexts.map((t) => t.trim()).find(Boolean)?.toUpperCase() ?? null;
  const masterOfText = reserved(SIDECAR_MASTER_OF_TARGET);
  const extra = { assetClass: reserved(SIDECAR_ASSET_CLASS_TARGET), masterVariant: reserved(SIDECAR_MASTER_VARIANT_TARGET), masterOf: masterOfText ? masterOfText.toUpperCase() : null };
  const values: Record<string, unknown> = {};
  for (const [fieldId, { element, texts: t }] of texts) {
    const field = def.fields.find((f) => f.id === fieldId)!;
    const v = coerceFieldValue(field, t);
    if (v === null) { problems.push(`${field.label}: “${t[0]?.slice(0, 60)}” does not fit a ${field.type} field`); continue; }
    values[fieldId] = v;
    matched.push({ fieldId, element });
  }
  // Keep only what validates on its own; required fields are not enforced for partial imports.
  const verdict = validateMetadata({ ...def, fields: def.fields.map((f) => ({ ...f, required: false })) }, values, null);
  if (!verdict.ok) {
    for (const [id, msg] of Object.entries(verdict.errors)) {
      problems.push(`${def.fields.find((f) => f.id === id)?.label ?? id}: ${msg}`);
      delete values[id];
    }
    return { values, assetCode, ...extra, matched: matched.filter((m) => m.fieldId in values), problems };
  }
  return { values: verdict.value, assetCode, ...extra, matched, problems };
}
